import type { ReactNode } from 'react';
import { ClockIcon, Settings2Icon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CustomDashboardTab, CustomDashboardTabGroup } from '@/lib/api/custom-dashboard';
import { CustomDashboardTabViewers } from './CustomDashboardTabViewers';
import { formatShortDateTime } from './formatShortDateTime';

export interface CustomDashboardPresentationProps {
  accountId: string;
  isNavigationBlocked?: boolean;
  authHeaders: Record<string, string>;
  onNavigation: () => void;
  onConfigureTabs?: () => void;
  /** ツールバー左端に置くアカウントの切り替え。 */
  accountSwitcher?: ReactNode;
  /** 表示するグループ（公開中のタブがあるグループだけ。表示順）。 */
  groups: CustomDashboardTabGroup[];
  /** 全グループのタブ（グループの順 → グループ内の順）。 */
  tabs: CustomDashboardTab[];
  activeTabId?: string;
  onTabChange: (tabId: string) => void;
}

/** 表示中のタブの公開 revision と最終更新日時（日時が読めなければ revision だけ）。 */
function revisionLabel(tab: CustomDashboardTab): string {
  const updatedAt = formatShortDateTime(tab.updatedAt);
  return updatedAt ? `r${tab.activeRevision} · ${updatedAt} 更新` : `r${tab.activeRevision}`;
}

export function CustomDashboardPresentation({
  accountId,
  isNavigationBlocked = false,
  authHeaders,
  onNavigation,
  onConfigureTabs,
  accountSwitcher,
  groups,
  tabs,
  activeTabId,
  onTabChange,
}: CustomDashboardPresentationProps) {
  // ?tab= の正規化前など activeTabId が tabs に無い瞬間は revision を出さない。
  const activeTab = activeTabId ? tabs.find((tab) => tab.tabId === activeTabId) : undefined;
  // 上段（グループ）は表示中のタブのグループを選ぶ。下段にはそのグループのタブだけを並べる。
  const activeGroup = groups.find((group) => group.groupId === activeTab?.groupId);
  const groupTabs = activeGroup ? tabs.filter((tab) => tab.groupId === activeGroup.groupId) : [];
  const handleGroupChange = (groupId: string) => {
    // グループを切り替えたら、そのグループの先頭のタブを表示する
    const first = tabs.find((tab) => tab.groupId === groupId);
    if (first) onTabChange(first.tabId);
  };
  return (
    // 地色はテンプレ本体（_shared/styles-common.css の --bg: #eef1f6）と揃え、
    // 下段のタブと iframe 内コンテンツを地続きに見せる。
    <div className="flex h-[calc(100vh-var(--app-header-h))] flex-col bg-[#eef1f6]">
      <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-x-4 border-b bg-white px-4">
        {accountSwitcher && (
          <>
            {accountSwitcher}
            <span aria-hidden className="h-6 w-px bg-border" />
          </>
        )}
        {groups.length > 0 && (
          // 上段のグループは下線タブ、下段のタブはピルにして、形で階層を区別する
          <Tabs
            value={activeGroup?.groupId}
            onValueChange={handleGroupChange}
            className="min-w-0 max-w-full overflow-x-auto"
          >
            <TabsList
              aria-label="タブグループ"
              className="h-12 w-max justify-start gap-1 rounded-none bg-transparent p-0"
            >
              {groups.map((group) => (
                <TabsTrigger
                  key={group.groupId}
                  value={group.groupId}
                  className="h-12 rounded-none border-b-2 border-transparent px-3 text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  {group.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        <div className="ml-auto flex items-center gap-3 py-1.5">
          {activeTab && (
            <span
              title="公開中の revision と最終更新日時"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground"
            >
              <ClockIcon aria-hidden className="size-3.5" />
              {revisionLabel(activeTab)}
            </span>
          )}
          {onConfigureTabs && (
            <Button type="button" variant="outline" size="sm" onClick={onConfigureTabs}>
              <Settings2Icon aria-hidden />
              タブ設定
            </Button>
          )}
        </div>
      </div>
      {groups.length > 0 && (
        <div className="shrink-0 overflow-x-auto px-4 py-2">
          <Tabs value={activeTabId} onValueChange={onTabChange}>
            <TabsList
              aria-label="タブ"
              className="h-[30px] w-max justify-start gap-1 bg-transparent p-0"
            >
              {groupTabs.map((tab) => (
                <TabsTrigger
                  key={tab.tabId}
                  value={tab.tabId}
                  className="h-[30px] rounded-lg px-3 text-[13px] font-normal text-muted-foreground hover:bg-white/60 hover:text-foreground data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.08),0_0_0_1px_rgba(15,23,42,.04)]"
                >
                  {tab.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {/*
              実際のタブ内容は CustomDashboardTabViewers（この Tabs の外側）が keep-alive で描画する。
              ここでは TabsTrigger の aria-controls が指す id を持たせるためだけに空の
              TabsContent を対応させ、余白は 0 にして見た目に影響しないようにする。
            */}
            {groupTabs.map((tab) => (
              <TabsContent key={tab.tabId} value={tab.tabId} className="m-0 h-0" />
            ))}
          </Tabs>
        </div>
      )}
      {isNavigationBlocked ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <Alert variant="destructive" className="max-w-xl">
            <AlertTitle>安全のため表示を停止しました</AlertTitle>
            <AlertDescription>ダッシュボード内で画面遷移が検知されました。</AlertDescription>
          </Alert>
        </div>
      ) : (
        // タブ経路は Page 側の isLoading/html に依存させない（M-1: 依存させるとタブ切替の
        // たびにこのコンテナごとアンマウントされ keep-alive が壊れる）。ローディング/エラーは
        // タブ単位で CustomDashboardTabViewers 配下の各スロットが自分の取得状態から表示する。
        <CustomDashboardTabViewers
          accountId={accountId}
          authHeaders={authHeaders}
          tabs={tabs}
          activeTabId={activeTabId}
          onNavigation={onNavigation}
        />
      )}
    </div>
  );
}
